import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, Priority } from '../types';
import { format, isPast, isToday, parseISO } from 'date-fns';

interface Props {
  card: Card;
  onClick: () => void;
  canDrag?: boolean;
}

const PRIORITY_CONFIG: Record<Priority, { label: string; color: string; bg: string }> = {
  low: { label: 'Low', color: '#64748B', bg: '#F1F5F9' },
  medium: { label: 'Medium', color: '#92400E', bg: '#FEF3C7' },
  high: { label: 'High', color: '#9A3412', bg: '#FED7AA' },
  urgent: { label: 'Urgent', color: '#DC2626', bg: '#FEF2F2' },
};

export default function TaskCard({ card, onClick, canDrag = true }: Props) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: card.id, disabled: !canDrag });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const priority = PRIORITY_CONFIG[card.priority as Priority] || PRIORITY_CONFIG.medium;
  const completedSubs = card.subTasks?.filter((s) => s.done).length || 0;
  const totalSubs = card.subTasks?.length || 0;

  // Parse date-only strings as local midnight (not UTC) to avoid off-by-one day display
  const dueDate = card.dueDate
    ? (() => {
        const s = String(card.dueDate).split('T')[0];
        const [y, m, d] = s.split('-').map(Number);
        return new Date(y, m - 1, d);
      })()
    : null;
  const isOverdue = dueDate && isPast(dueDate) && !isToday(dueDate);
  const isDueToday = dueDate && isToday(dueDate);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className="fade-in"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 12,
          padding: '12px 14px',
          boxShadow: isDragging
            ? '0 8px 24px rgba(0,0,0,0.15)'
            : '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.06)',
          border: '1px solid #F1F5F9',
          cursor: !canDrag ? 'pointer' : (isDragging ? 'grabbing' : 'grab'),
          userSelect: 'none',
          transition: 'box-shadow 0.15s, transform 0.15s',
        }}
        onMouseEnter={(e) => {
          if (!isDragging) {
            (e.currentTarget as HTMLElement).style.boxShadow =
              '0 4px 12px rgba(0,0,0,0.10), 0 8px 24px rgba(0,0,0,0.08)';
            (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)';
          }
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.boxShadow =
            '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.06)';
          (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
        }}
      >
        {/* Priority badge */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '2px 7px',
            borderRadius: 100,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: priority.color,
            background: priority.bg,
            marginBottom: 8,
          }}
        >
          {priority.label}
        </div>

        {/* Title */}
        <div
          style={{
            fontWeight: 600,
            fontSize: 13.5,
            color: '#1E293B',
            lineHeight: 1.4,
            marginBottom: 8,
          }}
        >
          {card.title}
        </div>

        {/* Project tag */}
        {card.project && (
          <div
            style={{
              fontSize: 11,
              color: '#64748B',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              marginBottom: 8,
            }}
          >
            <div
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: card.project.client?.color || '#2563EB',
              }}
            />
            <span className="truncate">{card.project.client?.name} · {card.project.name}</span>
          </div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
          {/* Due date */}
          {dueDate ? (
            <div
              style={{
                fontSize: 11,
                fontWeight: 500,
                color: isOverdue ? '#DC2626' : isDueToday ? '#D97706' : '#94A3B8',
                display: 'flex',
                alignItems: 'center',
                gap: 3,
              }}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <rect x="3" y="4" width="18" height="18" rx="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              {isOverdue ? 'Overdue · ' : isDueToday ? 'Today · ' : ''}
              {format(dueDate, 'MMM d')}
            </div>
          ) : (
            <div />
          )}

          {/* Subtasks + assignee */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {totalSubs > 0 && (
              <div
                style={{
                  fontSize: 11,
                  color: completedSubs === totalSubs ? '#16A34A' : '#94A3B8',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                }}
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="9 11 12 14 22 4"/>
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                </svg>
                {completedSubs}/{totalSubs}
              </div>
            )}
            {card.assignee && (
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 9,
                  fontWeight: 700,
                }}
                title={card.assignee.name}
              >
                {card.assignee.name.slice(0, 2).toUpperCase()}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
